import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import BreakingBadge, { LiveBadge } from "@/components/BreakingBadge";
import { useActiveBreakingId } from "@/lib/breaking";
import { useLang } from "@/lib/i18n";
import { api } from "@/lib/api";
import { getLatestFullText, stripInvisible } from "@/lib/articleText";
import { gazaTimeShort, groupByGazaDay, gazaDayDivider } from "@/lib/dateGaza";

const TEXT_STYLE = {
    whiteSpace: "normal",
    display: "-webkit-box", WebkitBoxOrient: "vertical", WebkitLineClamp: 3, overflow: "hidden",
    overflowWrap: "break-word",
    wordBreak: "normal",
    maxWidth: "100%",
    boxSizing: "border-box",
};

export default function LatestNewsWidget() {
    const activeBreakingId = useActiveBreakingId();
    const { p, lang, t, dir } = useLang();
    const [items, setItems] = useState([]);
    // Refetch MERGES with what live events already applied: a response that was in flight while a new
    // story arrived must never drop that story (stale list replacing a fresher one).
    const load = useCallback(() => {
        api.get("/public/latest?limit=10").then((r) => {
            const fetched = r.data || [];
            setItems((prev) => {
                const byId = new Map(fetched.map((x) => [x.id, x]));
                const oldest = fetched.length ? (fetched[fetched.length - 1].published_at || "") : "";
                for (const x of prev) if (!byId.has(x.id) && (x.published_at || "") >= oldest) byId.set(x.id, x);
                return [...byId.values()].sort((a, b) => (b.published_at || "").localeCompare(a.published_at || "")).slice(0, 20);
            });
        }).catch(() => {});
    }, []);
    useEffect(() => {
        load();

        const NEWS = new Set(["palestine", "world", "breaking"]);
        const applyIncoming = (incoming) => {
            incoming = (incoming || []).filter((x) => NEWS.has(x.section_slug));
            if (!incoming.length) return;
            setItems((prev) => {
                const seen = new Set(prev.map((x) => x.id));
                const fresh = incoming.filter((x) => !seen.has(x.id));
                if (!fresh.length) return prev;
                // Preserve existing sort by prepending only the ones missing.
                return [...fresh, ...prev].slice(0, 20);
            });
        };
        const applyUpdates = (updates) => {
            if (!updates || !updates.length) return;
            setItems((prev) => {
                const byId = new Map(prev.map((x) => [x.id, x]));
                for (const u of updates) if (byId.has(u.id)) byId.set(u.id, u);
                return Array.from(byId.values());
            });
        };
        const onUpdate = (e) => applyUpdates(e.detail || []);
        const onRemoved = (e) => { const gone = new Set(e.detail || []); if (gone.size) setItems((prev) => prev.filter((x) => !gone.has(x.id))); };
        // If the banner auto-applied, its detail is the array of items.
        // If the visitor clicked the banner, detail is undefined → refetch.
        const onApplyEvent = (e) => {
            if (Array.isArray(e.detail)) applyIncoming(e.detail);
            else load();
        };
        window.addEventListener("articles:apply", onApplyEvent);
        window.addEventListener("articles:update", onUpdate);
        window.addEventListener("articles:removed", onRemoved);
        // Fallback safety net — refresh every 60 s in case the poller missed something (skipped while the tab is hidden).
        const id = setInterval(() => { if (document.visibilityState === "visible") load(); }, 60000);
        return () => {
            clearInterval(id);
            window.removeEventListener("articles:apply", onApplyEvent);
            window.removeEventListener("articles:update", onUpdate);
            window.removeEventListener("articles:removed", onRemoved);
        };
    }, [load]);

    if (!items.length) return null;

    return (
        <aside
            dir={dir}
            data-testid="latest-news-widget"
            className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-sm shadow-sm overflow-hidden"
            style={{ boxSizing: "border-box", maxWidth: "100%" }}
        >
            {/* Scroll container — header stays visible via position:sticky */}
            <div
                className="overflow-y-auto overflow-x-hidden max-h-[600px] md:max-h-[650px]"
                data-testid="latest-scroll"
                style={{ boxSizing: "border-box" }}
            >
                <header
                    className="sticky top-0 z-10 bg-brand-red text-white px-4 py-2.5 flex items-center justify-between gap-2"
                    data-testid="latest-header"
                >
                    <h3 className="font-display font-black text-base leading-none">{t("latestNews")}</h3>
                    <LiveBadge testId="latest-live-badge" />
                </header>

                <ul className="divide-y divide-gray-100 dark:divide-gray-800" style={{ boxSizing: "border-box" }}>
                    {groupByGazaDay(items, "published_at").map((group) => {
                        const div = gazaDayDivider(group.sampleIso, lang);
                        return (
                            <li key={group.dayKey} className="contents">
                                {/* Day divider — flows naturally with scroll, never sticky.
                                   Uses a soft muted band with a small brand-red accent bar. */}
                                <div
                                    className="px-4 md:px-5 py-2.5 flex items-center gap-2
                                               bg-gray-50 dark:bg-gray-800/60
                                               border-y border-gray-100 dark:border-gray-800"
                                    data-testid={`latest-day-${group.dayKey}`}
                                    dir={dir}
                                >
                                    <span className="inline-block w-1 h-4 bg-brand-red rounded-sm" aria-hidden />
                                    <time
                                        dateTime={group.dayKey}
                                        className="text-[12.5px] md:text-[13px] font-bold text-brand-black dark:text-gray-200"
                                    >
                                        {div.prefix ? <><span className="text-brand-red">{div.prefix}</span> — {div.label}</> : div.label}
                                    </time>
                                </div>
                                <ul>
                                    {group.items.map((a) => {
                                        const text = stripInvisible(getLatestFullText(a)).replace(/\s*\n+\s*/g, " ").trim();
                                        const arOnly = lang !== "ar" && a.translation && !a.translation.available;
                                        return (
                                            <li key={a.id} className="group border-b border-gray-100 dark:border-gray-800 last:border-b-0">
                                                <Link
                                                    to={`${p}/article/${a.slug}`}
                                                    className="block px-4 py-4 md:px-5 md:py-4 transition-colors duration-150
                                                               hover:bg-gray-50 dark:hover:bg-gray-800/60
                                                               focus:outline-none focus-visible:bg-gray-50 dark:focus-visible:bg-gray-800/60"
                                                    data-testid={`latest-${a.slug}`}
                                                    style={{ boxSizing: "border-box" }}
                                                >
                                                    <div
                                                        className="mb-2 flex items-center gap-2 text-brand-red font-bold text-[12px] md:text-[12.5px] leading-none"
                                                        dir={dir}
                                                    >
                                                        <time dateTime={a.published_at || a.created_at} dir="ltr">{gazaTimeShort(a.published_at || a.created_at, lang)}</time>
                                                        {a.id === activeBreakingId && (
                                                            <BreakingBadge active testId={`latest-breaking-${a.slug}`} />
                                                        )}
                                                        {arOnly && <span className="pending-tr" dir="ltr" data-testid={`latest-pending-tr-${a.slug}`}>{t("arOnly")} · {t("pendingTr")}</span>}
                                                    </div>
                                                    <p
                                                        dir={arOnly ? "rtl" : dir}
                                                        className="text-[16px] md:text-[17px] lg:text-[17px]
                                                                   leading-[1.85]
                                                                   text-brand-black dark:text-gray-100
                                                                   transition-colors duration-150
                                                                   group-hover:text-brand-red"
                                                        style={{ ...TEXT_STYLE, textAlign: (arOnly || dir === "rtl") ? "right" : "left" }}
                                                    >
                                                        {text}
                                                    </p>
                                                </Link>
                                            </li>
                                        );
                                    })}
                                </ul>
                            </li>
                        );
                    })}
                </ul>
            </div>
        </aside>
    );
}
